/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import round from 'lodash/round';
import {
    announce,
    Button,
    FlexCol,
    FlexRow,
    Heading,
    Select,
    SelectOption,
    withLDConsumer,
} from 'sarsaparilla';
import { authModalActions } from 'shared-ui/auth/redux';
import StarDisplay from '../components/StarDisplay';
import { fetchAggregateSelector } from '../reducers/aggregate';

import { fetchAggregate as fetchAggregateAction } from '../actions/aggregate';
import {
    fetchPublicReviews as fetchPublicReviewsAction,
    resetPublicReviews as resetPublicReviewsAction,
} from '../actions/publicReviews';
import { fetchAIReview as fetchAIReviewAction } from '../actions/aiReview';
import RatingBreakout from '../components/RatingBreakout';
import CellCoverage from '../components/CellCoverage';
import LocationReviews from '../components/LocationReviews';
import { publicReviewsSelector } from '../reducers/publicReviews';
import { aiReviewSelector } from '../reducers/aiReview';
import type { aggregateResponse } from '../model/aggregate';
import type { PublicReviewType } from '../model/publicReview';
import type { flagRequest } from '../actions/reviewFlag';
import { postReviewFlag } from '../actions/reviewFlag';
import type { voteRequest } from '../actions/reviewVote';
import { postReviewVote } from '../actions/reviewVote';
import type { aiVoteRequest } from '../actions/aiReviewVote';
import { postAIReviewVote } from '../actions/aiReviewVote';
import { REVIEW_SORT_KEYS } from '../constants/constants';

type PropTypes = {
    location: {
        location_type: string;
        location_id: string;
    };
    startKey: string;
    reviewsLoading: number;
    reviews: Array<PublicReviewType>;
    fetchPublicReviews: Function;
    resetPublicReviews: Function;
    loading: boolean;
    aggregate?: aggregateResponse;
    fetchAggregate: Function;
    flagReview: Function;
    voteReview: Function;
    voteAIReview: Function;
    openLoginModal: () => void;
    className: string;
    noHeading: boolean;
    reviewLocationId: string;
    reviewLocationType: any;
    fetchAIReview: Function;
    aiReview: {
        location_id?: string;
        location_type?: string;
        summary?: string;
    } & Record<string, any>;
    aiReviewLoading: number;
    flags: {
        useAiReview: boolean;
    };
};

type State = {
    sortKey: string;
    rating: string;
    reviewDisplayLength: number;
    focusPosition: number | null;
};

export class LegacyInventoryReviewsSectionComponent extends React.PureComponent<
    PropTypes,
    State
> {
    static defaultProps = {
        className: '',
    };

    constructor(props: PropTypes) {
        super(props);

        this.handleSortChange = this.handleSortChange.bind(this);
    }

    state = {
        sortKey: 'MOST_RECENT',
        rating: '',
        reviewDisplayLength: 3,
        focusPosition: null,
    };

    componentDidMount() {
        const {
            loading,
            aggregate,
            location,
            resetPublicReviews,
            reviewsLoading,
            reviewLocationId,
            reviewLocationType,
        } = this.props;
        const { sortKey, rating } = this.state;

        if (
            !reviewsLoading ||
            location.location_id !== reviewLocationId ||
            location.location_type !== reviewLocationType
        ) {
            resetPublicReviews(
                {
                    locationId: location.location_id,
                    locationType: location.location_type,
                    pageSize: 10,
                    rating,
                    sortKey,
                    startKey: '',
                },
                this.isParentLocation()
            );
        }

        if (!loading || !aggregate || location.location_id !== aggregate.location_id) {
            this.props.fetchAggregate({
                locationType: location.location_type,
                locationId: location.location_id,
            });
        }

        this.maybeFetchAIReview();
    }

    componentDidUpdate(prevProps: PropTypes) {
        // LaunchDarkly flags resolve asynchronously; re-evaluate the AI fetch
        // when the flag flips on, or when the user navigates to a new location.
        const flagJustEnabled =
            !prevProps.flags?.useAiReview && this.props.flags?.useAiReview;
        const locationChanged =
            prevProps.location.location_id !== this.props.location.location_id ||
            prevProps.location.location_type !== this.props.location.location_type;

        if (flagJustEnabled || locationChanged) {
            this.maybeFetchAIReview();
        }
    }

    maybeFetchAIReview = () => {
        const { flags, fetchAIReview, aiReview, aiReviewLoading, location } = this.props;
        if (!flags?.useAiReview) return;
        if (aiReviewLoading) return;

        const cachedMatchesLocation =
            aiReview?.location_id === location.location_id &&
            aiReview?.location_type === location.location_type &&
            !!aiReview?.summary;
        if (cachedMatchesLocation) return;

        fetchAIReview({
            locationId: location.location_id,
            locationType: location.location_type,
        });
    };

    handleSortChange({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) {
        const { resetPublicReviews, location } = this.props;
        const { rating, reviewDisplayLength } = this.state;
        resetPublicReviews(
            {
                locationId: location.location_id,
                locationType: location.location_type,
                pageSize: reviewDisplayLength === 3 ? 10 : reviewDisplayLength + 10,
                rating,
                sortKey: value,
                startKey: '',
            },
            this.isParentLocation()
        );
        //@ts-ignore
        const announceValue = REVIEW_SORT_KEYS[value]
            ? //@ts-ignore
              `by ${REVIEW_SORT_KEYS[value]}`
            : '';
        announce(`The reviews list is now sorted ${announceValue}`);
        this.setState({ sortKey: value });
    }

    handleRatingFilterChange(value: string) {
        const { resetPublicReviews, location } = this.props;
        const { sortKey } = this.state;
        resetPublicReviews(
            {
                locationId: location.location_id,
                locationType: location.location_type,
                pageSize: 10,
                sortKey: sortKey.includes('RATING') ? 'MOST_RECENT' : sortKey,
                rating: value,
                startKey: '',
            },
            this.isParentLocation()
        );
        announce(`Showing reviews with rating of ${value} stars`);
        this.setState({
            reviewDisplayLength: 3,
            sortKey: sortKey.includes('RATING') ? 'MOST_RECENT' : sortKey,
            rating: value,
        });
    }

    handleViewExpansion() {
        const { fetchPublicReviews, startKey, reviews, location } = this.props;
        const { reviewDisplayLength, rating, sortKey } = this.state;
        let newLength = reviewDisplayLength === 3 ? 10 : reviewDisplayLength + 10;
        if (!startKey && newLength > reviews.length) {
            newLength = reviews.length;
        }
        this.setState({
            reviewDisplayLength: newLength,
            focusPosition: reviewDisplayLength,
        });
        if (startKey !== '') {
            fetchPublicReviews(
                {
                    locationId: location.location_id,
                    locationType: location.location_type,
                    sortKey,
                    rating,
                    startKey,
                    pageSize: 10,
                },
                this.isParentLocation()
            );
        }
    }

    handleReviewFlag(request: flagRequest) {
        const { flagReview, resetPublicReviews, reviews, location } = this.props;
        const { sortKey, rating } = this.state;
        flagReview(request)
            .then(() => {
                resetPublicReviews(
                    {
                        locationId: location.location_id,
                        locationType: location.location_type,
                        sortKey,
                        rating,
                        pageSize: reviews.length < 10 ? 10 : reviews.length,
                        startKey: '',
                    },
                    this.isParentLocation()
                );
            })
            .catch(() => {});
    }

    handleReviewVote(request: voteRequest) {
        const { voteReview, openLoginModal } = this.props;
        voteReview(request)
            .then(() => {
                const storageKey = request.id || request.reservation_id;
                if (storageKey) {
                    localStorage.setItem(storageKey, request.is_helpful.toString());
                }
                this.forceUpdate();
            })
            .catch((e: any) => {
                if (e.message === 'There are no claims in the auth token') {
                    openLoginModal();
                }
            });
    }

    handleAIReviewVote(request: aiVoteRequest) {
        const { voteAIReview, openLoginModal } = this.props;
        voteAIReview(request)
            .then(() => {
                localStorage.setItem(
                    request.reservation_id,
                    request.is_helpful.toString()
                );
                this.forceUpdate();
            })
            .catch((e: any) => {
                if (e.message === 'There are no claims in the auth token') {
                    openLoginModal();
                }
            });
    }

    isParentLocation() {
        const {
            location: { location_type: locationType },
        } = this.props;
        switch (locationType) {
            case 'Campground':
                return true;
            case 'Ticket Facility':
            case 'Timed Entry':
            case 'Venue Reservations':
                return true;
            default:
                return false;
        }
    }

    render() {
        const {
            aggregate,
            loading,
            startKey,
            reviews,
            reviewsLoading,
            className,
            noHeading,
            openLoginModal,
            aiReview,
        } = this.props;
        const { reviewDisplayLength, focusPosition, sortKey, rating } = this.state;
        if (
            loading ||
            !aggregate ||
            !aggregate.location_id ||
            aggregate.number_of_ratings <= 0
        ) {
            return null;
        }
        return (
            <div
                id="rating-review-inventory-review-section"
                className={`rating-review-inventory-review-section-wrapper ${className}`}
            >
                {!noHeading && (
                    <Heading
                        headingLevel={2}
                        appearance="h5"
                        id="rating-review-inventory-review-section-heading"
                        className="mb-2"
                        data-ga-tracking-id="086104295516"
                    >
                        Guest Reviews (
                        <span data-testid="guess-reviews">
                            {aggregate.number_of_ratings}
                        </span>
                        )
                    </Heading>
                )}
                <FlexRow>
                    <FlexCol lg={8}>
                        <FlexRow justifyContent="end">
                            <FlexCol sm={6} lg={5}>
                                <Select
                                    label="Sort By"
                                    isLabelVisible={false}
                                    value={sortKey}
                                    onChange={this.handleSortChange}
                                    id="reviews-sort"
                                >
                                    <SelectOption
                                        value="MOST_RECENT"
                                        gaTrackingId="207792271341"
                                    >
                                        Sort by: Most Recent
                                    </SelectOption>
                                    <SelectOption
                                        value="MOST_HELPFUL"
                                        gaTrackingId="207792271341"
                                    >
                                        Sort by: Most Helpful
                                    </SelectOption>
                                    {rating === '' && (
                                        <SelectOption
                                            value="HIGHEST_RATING"
                                            gaTrackingId="207792271341"
                                        >
                                            Sort by: Highest Rating
                                        </SelectOption>
                                    )}
                                    {rating === '' && (
                                        <SelectOption
                                            value="LOWEST_RATING"
                                            gaTrackingId="207792271341"
                                        >
                                            Sort by: Lowest Rating
                                        </SelectOption>
                                    )}
                                </Select>
                            </FlexCol>
                        </FlexRow>
                        <LocationReviews
                            displayLength={reviewDisplayLength}
                            focusPosition={focusPosition}
                            loading={reviewsLoading}
                            reviews={reviews}
                            aiReview={aiReview}
                            voteReview={this.handleReviewVote.bind(this)}
                            flagReview={this.handleReviewFlag.bind(this)}
                            voteAIReview={this.handleAIReviewVote.bind(this)}
                            openLoginModal={openLoginModal}
                        />
                        <FlexRow className="location-reviews-button">
                            {!!reviews &&
                                (startKey !== '' ||
                                    reviews.length > reviewDisplayLength) && (
                                    <Button
                                        onClick={this.handleViewExpansion.bind(this)}
                                        appearance="tertiary"
                                        data-testid="button-see-more-reviews"
                                        gaTrackingId="345490145715"
                                    >
                                        See More Reviews
                                    </Button>
                                )}
                        </FlexRow>
                    </FlexCol>
                    <FlexCol className="left-rail" lg={4} order="first">
                        <div className="rating-wrap" data-notranslate>
                            {round(aggregate.average_rating, 1)}
                            <StarDisplay value={aggregate.average_rating} />
                        </div>
                        <h3 className="rec-sr-only">Review Filter</h3>
                        <RatingBreakout
                            total={aggregate.number_of_ratings}
                            starCounts={aggregate.star_counts}
                            value={rating}
                            onChange={this.handleRatingFilterChange.bind(this)}
                        />
                        {!!aggregate.aggregate_cell_coverage_ratings && (
                            <CellCoverage
                                cellRatings={aggregate.aggregate_cell_coverage_ratings}
                            />
                        )}
                    </FlexCol>
                </FlexRow>
            </div>
        );
    }
}

const mapStateToProps = (
    state: any,
    ownProps: { locationType: any; locationId: string }
) => {
    const { loading, data: aggregate } = fetchAggregateSelector(state);
    const {
        data: reviews,
        startKey,
        loading: reviewsLoading,
        location_id: reviewLocationId,
        location_type: reviewLocationType,
    }: Record<string, any> = publicReviewsSelector(state);
    const { loading: aiReviewLoading, data: aiReview }: Record<string, any> =
        aiReviewSelector(state);
    const { locationType, locationId } = ownProps;
    return {
        location: { location_id: locationId, location_type: locationType },
        startKey,
        loading,
        reviews,
        reviewsLoading,
        reviewLocationId,
        reviewLocationType,
        aggregate,
        aiReviewLoading,
        aiReview,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
    bindActionCreators(
        {
            fetchAggregate: fetchAggregateAction,
            fetchPublicReviews: fetchPublicReviewsAction,
            resetPublicReviews: resetPublicReviewsAction,
            fetchAIReview: fetchAIReviewAction,
            flagReview: postReviewFlag,
            voteReview: postReviewVote,
            voteAIReview: postAIReviewVote,
            openLoginModal: authModalActions.openLoginModal,
        },
        dispatch
    );

const LegacyInventoryReviewsSectionContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(LegacyInventoryReviewsSectionComponent);

export default withLDConsumer()(LegacyInventoryReviewsSectionContainer);
